// 11 §7 keyboard grammar (default keys; every one is rebindable, docs/87): Tab cycles heroes, B the bonus slot, the number row picks the hotbar's
// actions, Enter confirms, Esc backs out, Shift+arrows move the tile cursor (plain arrows and WASD
// pan the camera, docs/33 E6, and Q / E zoom it, docs/42 — the scene reads those), Space ready, P pause, F filters, +/− zoom,
// C centres the selected hero, V fits the board, M minimap, I the inventory (docs/40); Z pings the square
// under the pointer (Shift+Z: danger), Ctrl/Cmd+Z undoes the last planned slot, K opens the selected
// hero's stats card and G its gear (docs/83).
// Decision C6: keyboard is a release requirement.
import type { AuthoringEvent } from './stateMachine';
import { HOTBAR_MORE, HOTBAR_SLOTS, hotbarId, hotkeyFor, type Overrides } from '../input/hotkeys';

export type ViewCommand =
  | { type: 'zoom'; delta: 1 | -1 }
  | { type: 'frame'; kind: 'fit' | 'hero' }
  | { type: 'cycleFilter' }
  | { type: 'toggleMinimap' }
  | { type: 'toggleReady' }
  | { type: 'togglePause' }
  | { type: 'toggleInventory' }
  | { type: 'ping'; danger: boolean }
  | { type: 'heroSheet' }
  | { type: 'heroGear' }
  | { type: 'undo' };

export type KeyCommand = { kind: 'authoring'; event: AuthoringEvent } | { kind: 'view'; command: ViewCommand } | { kind: 'hotkey'; key: string };

/** docs/87: each hotkey of the encounter's grammar and what it does (the keys live in `core/input/hotkeys.ts`). */
const COMMANDS: ReadonlyArray<[string, KeyCommand]> = [
  ['board.undo', { kind: 'view', command: { type: 'undo' } }],
  ['board.nextHero', { kind: 'authoring', event: { type: 'cycleHero', dir: 1 } }],
  ['board.prevHero', { kind: 'authoring', event: { type: 'cycleHero', dir: -1 } }],
  ['board.confirm', { kind: 'authoring', event: { type: 'confirm' } }],
  ['board.back', { kind: 'authoring', event: { type: 'escape' } }],
  ['board.cursorUp', { kind: 'authoring', event: { type: 'moveCursor', dx: 0, dy: -1 } }],
  ['board.cursorDown', { kind: 'authoring', event: { type: 'moveCursor', dx: 0, dy: 1 } }],
  ['board.cursorLeft', { kind: 'authoring', event: { type: 'moveCursor', dx: -1, dy: 0 } }],
  ['board.cursorRight', { kind: 'authoring', event: { type: 'moveCursor', dx: 1, dy: 0 } }],
  ['board.bonus', { kind: 'authoring', event: { type: 'chooseSlot', slot: 'B' } }],
  ['board.hold', { kind: 'authoring', event: { type: 'hold' } }],
  ['board.ready', { kind: 'view', command: { type: 'toggleReady' } }],
  ['board.pause', { kind: 'view', command: { type: 'togglePause' } }],
  ['board.filter', { kind: 'view', command: { type: 'cycleFilter' } }],
  ['board.minimap', { kind: 'view', command: { type: 'toggleMinimap' } }],
  ['board.inventory', { kind: 'view', command: { type: 'toggleInventory' } }],
  ['board.heroSheet', { kind: 'view', command: { type: 'heroSheet' } }],
  ['board.heroGear', { kind: 'view', command: { type: 'heroGear' } }],
  ['board.ping', { kind: 'view', command: { type: 'ping', danger: false } }],
  ['board.warn', { kind: 'view', command: { type: 'ping', danger: true } }],
  ['camera.hero', { kind: 'view', command: { type: 'frame', kind: 'hero' } }],
  ['camera.fit', { kind: 'view', command: { type: 'frame', kind: 'fit' } }],
  ['camera.zoomIn', { kind: 'view', command: { type: 'zoom', delta: 1 } }],
  ['camera.zoomOut', { kind: 'view', command: { type: 'zoom', delta: -1 } }],
  // docs/33 E3: the number row is the hotbar's (1/2 = slots in HeroSelected, else hotbar); 0 is Hold
  ...[...HOTBAR_SLOTS, ...HOTBAR_MORE].map((n): [string, KeyCommand] => [hotbarId(n), { kind: 'hotkey', key: n }]),
];
const COMMAND_IDS = COMMANDS.map(([id]) => id);
const COMMAND_OF = new Map(COMMANDS);

export function keyToCommand(e: Pick<KeyboardEvent, 'code' | 'shiftKey' | 'ctrlKey' | 'metaKey' | 'altKey'>, overrides?: Overrides): KeyCommand | undefined {
  const id = hotkeyFor(e, COMMAND_IDS, overrides);
  return id ? COMMAND_OF.get(id) : undefined;
}
